import { useState, useEffect, useCallback } from "react";
import { Routes, Route } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { AttachmentList, AttachmentBadge } from "@/components/FileUpload";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, getDay, addMonths, subMonths } from "date-fns";
import {
  FolderKanban, Clock, CalendarIcon, Loader2, User, Save,
  ChevronLeft, ChevronRight, Briefcase, Home, Coffee, Heart, Baby, Plane, X, KeyRound,
  Paperclip, Download, FileText, Eye
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Swedish Public Holidays 2024-2026
const SWEDISH_HOLIDAYS = [
  // 2024
  { date: "2024-01-01", name: "Nyårsdagen" },
  { date: "2024-01-06", name: "Trettondedag jul" },
  { date: "2024-03-29", name: "Långfredagen" },
  { date: "2024-03-31", name: "Påskdagen" },
  { date: "2024-04-01", name: "Annandag påsk" },
  { date: "2024-05-01", name: "Första maj" },
  { date: "2024-05-09", name: "Kristi himmelsfärdsdag" },
  { date: "2024-05-19", name: "Pingstdagen" },
  { date: "2024-06-06", name: "Sveriges nationaldag" },
  { date: "2024-06-21", name: "Midsommarafton" },
  { date: "2024-06-22", name: "Midsommardagen" },
  { date: "2024-11-02", name: "Alla helgons dag" },
  { date: "2024-12-24", name: "Julafton" },
  { date: "2024-12-25", name: "Juldagen" },
  { date: "2024-12-26", name: "Annandag jul" },
  { date: "2024-12-31", name: "Nyårsafton" },
  // 2025
  { date: "2025-01-01", name: "Nyårsdagen" },
  { date: "2025-01-06", name: "Trettondedag jul" },
  { date: "2025-04-18", name: "Långfredagen" },
  { date: "2025-04-20", name: "Påskdagen" },
  { date: "2025-04-21", name: "Annandag påsk" },
  { date: "2025-05-01", name: "Första maj" },
  { date: "2025-05-29", name: "Kristi himmelsfärdsdag" },
  { date: "2025-06-06", name: "Sveriges nationaldag" },
  { date: "2025-06-08", name: "Pingstdagen" },
  { date: "2025-06-20", name: "Midsommarafton" },
  { date: "2025-06-21", name: "Midsommardagen" },
  { date: "2025-11-01", name: "Alla helgons dag" },
  { date: "2025-12-24", name: "Julafton" },
  { date: "2025-12-25", name: "Juldagen" },
  { date: "2025-12-26", name: "Annandag jul" },
  { date: "2025-12-31", name: "Nyårsafton" },
  // 2026
  { date: "2026-01-01", name: "Nyårsdagen" },
  { date: "2026-01-06", name: "Trettondedag jul" },
  { date: "2026-04-03", name: "Långfredagen" },
  { date: "2026-04-05", name: "Påskdagen" },
  { date: "2026-04-06", name: "Annandag påsk" },
  { date: "2026-05-01", name: "Första maj" },
  { date: "2026-05-14", name: "Kristi himmelsfärdsdag" },
  { date: "2026-05-24", name: "Pingstdagen" },
  { date: "2026-06-06", name: "Sveriges nationaldag" },
  { date: "2026-06-19", name: "Midsommarafton" },
  { date: "2026-06-20", name: "Midsommardagen" },
  { date: "2026-10-31", name: "Alla helgons dag" },
  { date: "2026-12-24", name: "Julafton" },
  { date: "2026-12-25", name: "Juldagen" },
  { date: "2026-12-26", name: "Annandag jul" },
  { date: "2026-12-31", name: "Nyårsafton" },
];

// Entry types - simplified: work or leave
const ENTRY_TYPES = [
  { value: "work", label: "Work", icon: Briefcase, color: "bg-[#F37021]" },
  { value: "sick", label: "Sick (Sjuk)", icon: Heart, color: "bg-red-500" },
  { value: "pappaledigt", label: "Pappaledigt", icon: Baby, color: "bg-pink-500" },
  { value: "vab", label: "VAB", icon: Baby, color: "bg-orange-400" },
  { value: "semester", label: "Semester", icon: Plane, color: "bg-green-500" },
  { value: "off", label: "Off / Ledig", icon: X, color: "bg-gray-500" },
  { value: "other_leave", label: "Other Leave", icon: Coffee, color: "bg-gray-400" },
];

const isHoliday = (date) => {
  const dateStr = format(date, "yyyy-MM-dd");
  return SWEDISH_HOLIDAYS.find(h => h.date === dateStr);
};

const isWeekend = (date) => {
  const day = getDay(date);
  return day === 0 || day === 6;
};

// Worker Dashboard Overview
const WorkerOverview = () => {
  const { user, updateUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    personal_number: user?.personal_number || "",
    address: user?.address || ""
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    // Show password change prompt on first login if flag is set
    if (user?.password_change_required) {
      setShowPasswordPrompt(true);
    }
  }, [user, fetchStats]);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || "",
        phone: user.phone || "",
        personal_number: user.personal_number || "",
        address: user.address || ""
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    try {
      const response = await axios.put(`${API}/auth/me`, profileData);
      updateUser(response.data);
      setEditMode(false);
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success("Password changed successfully");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
      setShowPasswordPrompt(false);
      // Update user state to clear the flag
      updateUser({ password_change_required: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const dismissPasswordPrompt = async () => {
    try {
      await axios.post(`${API}/auth/dismiss-password-prompt`);
      setShowPasswordPrompt(false);
      updateUser({ password_change_required: false });
    } catch (error) {
      setShowPasswordPrompt(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
      </div>
    );
  }

  return (
    <div data-testid="worker-dashboard">
      {/* First Login Password Change Prompt */}
      <Dialog open={showPasswordPrompt} onOpenChange={setShowPasswordPrompt}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#F37021]" />
              Change Your Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Your password was set by your supervisor. For security, we recommend changing it to something only you know.
              </p>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label className="input-label">Current Password</Label>
                <Input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  data-testid="prompt-current-password"
                />
              </div>
              <div>
                <Label className="input-label">New Password</Label>
                <Input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="prompt-new-password"
                />
              </div>
              <div>
                <Label className="input-label">Confirm New Password</Label>
                <Input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  required
                  data-testid="prompt-confirm-password"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 btn-primary" disabled={changingPassword}>
                  {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  Change Password
                </Button>
                <Button type="button" variant="outline" onClick={dismissPasswordPrompt}>
                  Later
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <h1 className="text-4xl font-bold text-[#333333] mb-2">MY DASHBOARD</h1>
      <p className="text-[#52525B] mb-8">Welcome, {user?.name}</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 stagger-children">
        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold uppercase text-[#52525B]">My Assignments</p>
                <p className="text-3xl font-bold text-[#333333] mt-1">{stats?.my_assignments || 0}</p>
              </div>
              <FolderKanban className="w-10 h-10 text-[#F37021]" />
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold uppercase text-[#52525B]">Total Entries</p>
                <p className="text-3xl font-bold text-[#333333] mt-1">{stats?.my_timesheets || 0}</p>
              </div>
              <Clock className="w-10 h-10 text-[#F37021]" />
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold uppercase text-[#52525B]">This Week</p>
                <p className="text-3xl font-bold text-[#333333] mt-1">{stats?.this_week_entries || 0}</p>
              </div>
              <CalendarIcon className="w-10 h-10 text-[#F37021]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Profile Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl flex items-center gap-2">
            <User className="w-5 h-5" />
            MY PROFILE
          </CardTitle>
          <Button
            variant="outline"
            onClick={() => editMode ? handleSaveProfile() : setEditMode(true)}
            data-testid="edit-profile-button"
          >
            {editMode ? (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            ) : (
              "Edit"
            )}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="input-label">Full Name</Label>
              <Input
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                disabled={!editMode}
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <Label className="input-label">Email</Label>
              <Input value={user?.email} disabled />
            </div>
            <div>
              <Label className="input-label">Personal Number</Label>
              <Input
                value={profileData.personal_number}
                onChange={(e) => setProfileData({ ...profileData, personal_number: e.target.value })}
                disabled={!editMode}
                placeholder="YYYYMMDD-XXXX"
                data-testid="profile-personal-number-input"
              />
            </div>
            <div>
              <Label className="input-label">Phone</Label>
              <Input
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                disabled={!editMode}
                data-testid="profile-phone-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="input-label">Address</Label>
              <Input
                value={profileData.address}
                onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                disabled={!editMode}
                data-testid="profile-address-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            CHANGE PASSWORD
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <Label className="input-label">Current Password</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                required
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label className="input-label">New Password</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label className="input-label">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
                data-testid="confirm-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="btn-primary"
              disabled={changingPassword}
              data-testid="change-password-button"
            >
              {changingPassword ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <KeyRound className="w-4 h-4 mr-2" />
              )}
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Calendar View Component
const WorkerCalendar = () => {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [timesheets, setTimesheets] = useState([]);
  const [assignedAgreements, setAssignedAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    entry_type: "work",  // "work" or leave types
    a_number: "",        // A-number or "Depot"
    start_time: "08:00",
    end_time: "17:00",
    location: "",
    worked_with: "",
    detailed_comments: "",
    notes: "",
    leave_reason: ""
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = format(startOfMonth(currentMonth), "yyyy-MM-dd");
      const endDate = format(endOfMonth(currentMonth), "yyyy-MM-dd");
      
      // Fetch timesheets and worker's assigned agreements (no projects)
      const [tsRes, agreementsRes] = await Promise.all([
        axios.get(`${API}/timesheets?start_date=${startDate}&end_date=${endDate}`),
        axios.get(`${API}/agreements/for-worker/${user?.id}`)
      ]);
      setTimesheets(tsRes.data);
      setAssignedAgreements(agreementsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [currentMonth, user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const getEntryForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return timesheets.find(t => t.date === dateStr);
  };

  const handleDayClick = (date) => {
    const holiday = isHoliday(date);
    if (holiday) {
      toast.info(`${holiday.name} - Swedish Holiday`);
      return;
    }
    
    setSelectedDate(date);
    setFormData({
      entry_type: "work",
      a_number: "",
      start_time: "08:00",
      end_time: "17:00",
      location: "",
      worked_with: "",
      detailed_comments: "",
      notes: "",
      leave_reason: ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isLeaveType = ["sick", "pappaledigt", "vab", "semester", "off", "other_leave"].includes(formData.entry_type);
    
    try {
      // Determine location based on selection
      let locationValue = formData.location;
      if (formData.a_number === "Depot") {
        locationValue = formData.location || "Depot";
      }
      
      await axios.post(`${API}/timesheets`, {
        a_number: isLeaveType ? formData.entry_type.toUpperCase() : formData.a_number,
        date: format(selectedDate, "yyyy-MM-dd"),
        start_time: isLeaveType ? "00:00" : formData.start_time,
        end_time: isLeaveType ? "00:00" : formData.end_time,
        location: isLeaveType ? formData.entry_type : locationValue,
        worked_with: formData.worked_with,
        detailed_comments: isLeaveType 
          ? `${ENTRY_TYPES.find(t => t.value === formData.entry_type)?.label}: ${formData.leave_reason || 'No details'}`
          : formData.detailed_comments,
        notes: formData.notes || "",
        entry_type: isLeaveType ? formData.entry_type : "work"
      });
      
      toast.success("Entry saved!");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save entry");
    }
  };

  const isLeaveType = ["sick", "pappaledigt", "vab", "semester", "off", "other_leave"].includes(formData.entry_type);
  const isWorkType = formData.entry_type === "work";

  // Get first day offset for calendar grid
  const firstDayOfMonth = startOfMonth(currentMonth);
  const startOffset = getDay(firstDayOfMonth); // 0 = Sunday, 1 = Monday, etc.
  // Adjust for Monday start (Swedish calendar)
  const mondayOffset = startOffset === 0 ? 6 : startOffset - 1;

  return (
    <div data-testid="worker-calendar">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-bold text-[#333333]">CALENDAR</h1>
          <p className="text-[#52525B]">Click any day to add your timesheet</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-lg font-bold min-w-[150px] text-center">
            {format(currentMonth, "MMMM yyyy")}
          </span>
          <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Legend */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            {ENTRY_TYPES.map(type => (
              <div key={type.value} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${type.color}`} />
                <span className="text-sm">{type.label}</span>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <span className="text-sm">Holiday</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
            </div>
          ) : (
            <>
              {/* Day headers - Monday start */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"].map(day => (
                  <div key={day} className="text-center text-sm font-bold text-[#52525B] py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells for offset */}
                {Array.from({ length: mondayOffset }).map((_, i) => (
                  <div key={`empty-${i}`} className="h-24" />
                ))}
                
                {days.map(day => {
                  const entry = getEntryForDate(day);
                  const holiday = isHoliday(day);
                  const weekend = isWeekend(day);
                  const entryType = entry ? ENTRY_TYPES.find(t => t.value === entry.entry_type) : null;
                  
                  return (
                    <div
                      key={day.toISOString()}
                      onClick={() => handleDayClick(day)}
                      className={`
                        h-24 p-2 border rounded-md cursor-pointer transition-all hover:shadow-md
                        ${isToday(day) ? 'ring-2 ring-[#F37021]' : ''}
                        ${holiday ? 'bg-yellow-50 border-yellow-300' : weekend ? 'bg-gray-50' : 'bg-white'}
                        ${entry ? 'border-l-4' : ''}
                      `}
                      style={entry && entryType ? { borderLeftColor: entryType.color.replace('bg-', '').includes('[') ? '#F37021' : '' } : {}}
                      data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                    >
                      <div className="flex justify-between items-start">
                        <span className={`text-sm font-medium ${isToday(day) ? 'text-[#F37021]' : ''}`}>
                          {format(day, "d")}
                        </span>
                        {holiday && (
                          <span className="text-xs bg-yellow-400 text-yellow-900 px-1 rounded">H</span>
                        )}
                      </div>
                      
                      {holiday && (
                        <p className="text-xs text-yellow-700 mt-1 truncate">{holiday.name}</p>
                      )}
                      
                      {entry && entryType && (
                        <div className={`mt-1 p-1 rounded text-xs text-white ${entryType.color}`}>
                          <div className="flex items-center gap-1">
                            <entryType.icon className="w-3 h-3" />
                            <span className="truncate">
                              {entry.entry_type === 'work' 
                                ? (entry.a_number === 'Depot' ? 'Depot' : entry.agreement_name || entry.a_number) 
                                : entryType.label}
                            </span>
                          </div>
                          {entry.start_time !== "00:00" && (
                            <p className="text-xs opacity-80">{entry.start_time}-{entry.end_time}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Entry Type Selection - Work or Leave */}
            <div>
              <Label className="input-label">Entry Type</Label>
              <Select
                value={formData.entry_type}
                onValueChange={(value) => setFormData({ ...formData, entry_type: value, a_number: "" })}
              >
                <SelectTrigger data-testid="entry-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="work">🔧 Work</SelectItem>
                  <SelectItem value="sick">❤️ Sick (Sjuk)</SelectItem>
                  <SelectItem value="pappaledigt">👶 Pappaledigt</SelectItem>
                  <SelectItem value="vab">👶 VAB</SelectItem>
                  <SelectItem value="semester">✈️ Semester</SelectItem>
                  <SelectItem value="off">⛔ Off / Ledig</SelectItem>
                  <SelectItem value="other_leave">📝 Other Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Work-specific fields */}
            {isWorkType && (
              <>
                {/* Unified A-Number / Depot dropdown */}
                <div>
                  <Label className="input-label">Assignment *</Label>
                  <Select
                    value={formData.a_number}
                    onValueChange={(value) => {
                      const agreement = assignedAgreements.find(a => a.a_number === value);
                      setFormData({ 
                        ...formData, 
                        a_number: value,
                        location: value === "Depot" ? "" : (agreement?.location || formData.location)
                      });
                    }}
                  >
                    <SelectTrigger data-testid="a-number-select">
                      <SelectValue placeholder="Select assignment or Depot" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Depot is always available as a fixed option */}
                      <SelectItem value="Depot">
                        <div className="flex items-center gap-2">
                          <Home className="w-4 h-4 text-blue-500" />
                          <span>Depot Work</span>
                        </div>
                      </SelectItem>
                      
                      {/* Divider if there are assignments */}
                      {assignedAgreements.length > 0 && (
                        <div className="border-t my-1" />
                      )}
                      
                      {/* Assigned A-numbers */}
                      {assignedAgreements.map((a) => (
                        <SelectItem key={a.id} value={a.a_number}>
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-[#F37021]" />
                            <span>{a.a_number} - {a.project_name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {assignedAgreements.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      No project assignments yet. Select &quot;Depot Work&quot; or contact your supervisor.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="input-label">Start Time</Label>
                    <Input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      required
                      data-testid="start-time-input"
                    />
                  </div>
                  <div>
                    <Label className="input-label">End Time</Label>
                    <Input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      required
                      data-testid="end-time-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="input-label">Location {formData.a_number === "Depot" ? "*" : ""}</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder={formData.a_number === "Depot" ? "Depot location" : "Work location (auto-filled from assignment)"}
                    required={formData.a_number === "Depot"}
                    data-testid="location-input"
                  />
                </div>

                <div>
                  <Label className="input-label">Worked With (who did you work with?)</Label>
                  <Input
                    value={formData.worked_with}
                    onChange={(e) => setFormData({ ...formData, worked_with: e.target.value })}
                    placeholder="e.g., Erik and Maria from maintenance team"
                    data-testid="worked-with-input"
                  />
                </div>

                <div>
                  <Label className="input-label">Work Details *</Label>
                  <Textarea
                    value={formData.detailed_comments}
                    onChange={(e) => setFormData({ ...formData, detailed_comments: e.target.value })}
                    placeholder="Describe in detail what you did..."
                    rows={4}
                    required
                    data-testid="work-details-input"
                  />
                </div>

                <div>
                  <Label className="input-label">Notes (optional)</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Delays, site issues, extra work..."
                    data-testid="notes-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Short note for any issues or special circumstances</p>
                </div>
              </>
            )}

            {/* Leave-specific fields */}
            {isLeaveType && (
              <div>
                <Label className="input-label">Reason / Notes (optional)</Label>
                <Textarea
                  value={formData.leave_reason}
                  onChange={(e) => setFormData({ ...formData, leave_reason: e.target.value })}
                  placeholder="Any additional details..."
                  rows={3}
                  data-testid="leave-reason-input"
                />
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={isWorkType && !formData.a_number}
              data-testid="save-entry-button"
            >
              Save Entry
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Worker History View
const WorkerHistory = () => {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTimesheets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/timesheets`);
      setTimesheets(response.data);
    } catch (error) {
      toast.error("Failed to load timesheets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API}/timesheets/${id}`);
      toast.success("Entry deleted");
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete entry");
    }
  };

  return (
    <div data-testid="worker-history">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-[#333333]">HISTORY</h1>
        <p className="text-[#52525B]">View all your entries</p>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
            </div>
          ) : (
            <Table className="table-aggreko">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Assignment</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {timesheets.map((ts) => {
                  const entryType = ENTRY_TYPES.find(t => t.value === ts.entry_type);
                  return (
                    <TableRow key={ts.id} data-testid={`history-row-${ts.id}`}>
                      <TableCell>{ts.date}</TableCell>
                      <TableCell>
                        {entryType && (
                          <Badge className={`${entryType.color} text-white`}>
                            {entryType.label}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {ts.entry_type === 'work' 
                          ? (ts.a_number === 'Depot' 
                              ? <span className="flex items-center gap-1"><Home className="w-4 h-4 text-blue-500" /> Depot</span>
                              : <span>{ts.a_number} {ts.agreement_name && `- ${ts.agreement_name}`}</span>)
                          : ts.location}
                      </TableCell>
                      <TableCell>
                        {ts.start_time !== "00:00" ? `${ts.start_time} - ${ts.end_time}` : "-"}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{ts.detailed_comments}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500"
                          onClick={() => handleDelete(ts.id)}
                          data-testid={`delete-entry-${ts.id}`}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {timesheets.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-[#A1A1AA]">
                      No entries yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// My Jobs/Assignments Page - View job details and attachments
const MyAssignments = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [viewDialog, setViewDialog] = useState(false);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const fetchAssignments = useCallback(async () => {
    try {
      // Get worker requests where this worker is assigned
      const response = await axios.get(`${API}/worker/my-assignments`);
      setAssignments(response.data || []);
    } catch (error) {
      // If endpoint doesn't exist yet, try getting agreements for worker
      try {
        const agreementsRes = await axios.get(`${API}/agreements/for-worker/${user?.id}`);
        // Convert agreements to assignment format
        const assignmentData = agreementsRes.data.map(a => ({
          id: a.id,
          agreement_a_number: a.a_number,
          agreement_project_name: a.project_name,
          location_name: a.location,
          start_date: a.start_date,
          status: 'active'
        }));
        setAssignments(assignmentData);
      } catch (err) {
        console.error("Failed to load assignments");
        setAssignments([]);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const openViewDialog = async (assignment) => {
    setSelectedAssignment(assignment);
    setLoadingAttachments(true);
    
    try {
      // Get all attachments for this request
      const attachmentsRes = await axios.get(`${API}/worker-requests/${assignment.id}/all-attachments`);
      setAttachments([
        ...(attachmentsRes.data.request_attachments || []),
        ...(attachmentsRes.data.agreement_attachments || [])
      ]);
    } catch (error) {
      console.error("Failed to load attachments");
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
    
    setViewDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-assignments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#333333]">MY ASSIGNMENTS</h1>
          <p className="text-[#A1A1AA]">View your assigned jobs and download documents</p>
        </div>
      </div>

      <div className="grid gap-4">
        {assignments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Briefcase className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No active assignments</p>
              <p className="text-sm text-gray-400 mt-2">Check back later or contact your supervisor</p>
            </CardContent>
          </Card>
        ) : (
          assignments.map((assignment) => (
            <Card key={assignment.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xl font-bold text-[#F37021]">
                        {assignment.agreement_a_number}
                      </span>
                      <Badge className="bg-green-100 text-green-800">
                        {assignment.status || 'Active'}
                      </Badge>
                    </div>
                    <p className="text-lg font-medium text-gray-800">
                      {assignment.agreement_project_name}
                    </p>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                      {assignment.location_name && (
                        <span>📍 {assignment.location_name}</span>
                      )}
                      {assignment.start_date && (
                        <span>📅 Starts: {assignment.start_date}</span>
                      )}
                      {assignment.num_days && (
                        <span>⏱️ {assignment.num_days} days</span>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={() => openViewDialog(assignment)}
                    className="ml-4"
                    data-testid={`view-assignment-${assignment.id}`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View & Documents
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* View Assignment Details Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">JOB DETAILS</DialogTitle>
          </DialogHeader>
          
          {selectedAssignment && (
            <div className="space-y-4">
              {/* A-Number Header */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="text-xs text-orange-600 uppercase mb-1">A-Number</p>
                <p className="text-2xl font-bold text-[#F37021]">{selectedAssignment.agreement_a_number}</p>
                <p className="text-lg text-orange-700">{selectedAssignment.agreement_project_name}</p>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                {selectedAssignment.start_date && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase">Start Date</p>
                    <p className="text-lg font-medium">{selectedAssignment.start_date}</p>
                  </div>
                )}
                {selectedAssignment.num_days && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase">Duration</p>
                    <p className="text-lg font-medium">{selectedAssignment.num_days} days</p>
                  </div>
                )}
                {selectedAssignment.location_name && (
                  <div className="bg-gray-50 p-3 rounded-lg col-span-2">
                    <p className="text-xs text-gray-500 uppercase">Location</p>
                    <p className="text-lg font-medium">{selectedAssignment.location_name}</p>
                    {selectedAssignment.address && (
                      <p className="text-sm text-gray-600">{selectedAssignment.address}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Contact Info */}
              {(selectedAssignment.contact_person_onsite_name || selectedAssignment.project_engineer_name) && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-xs text-blue-600 uppercase mb-2">Contacts</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedAssignment.contact_person_onsite_name && (
                      <div>
                        <p className="text-sm text-gray-500">Site Contact</p>
                        <p className="font-medium">{selectedAssignment.contact_person_onsite_name}</p>
                        {selectedAssignment.contact_person_onsite_phone && (
                          <p className="text-sm text-blue-600">📞 {selectedAssignment.contact_person_onsite_phone}</p>
                        )}
                      </div>
                    )}
                    {selectedAssignment.project_engineer_name && (
                      <div>
                        <p className="text-sm text-gray-500">Project Engineer</p>
                        <p className="font-medium">{selectedAssignment.project_engineer_name}</p>
                        {selectedAssignment.project_engineer_phone && (
                          <p className="text-sm text-blue-600">📞 {selectedAssignment.project_engineer_phone}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedAssignment.description && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase mb-1">Job Description</p>
                  <p className="text-sm">{selectedAssignment.description}</p>
                </div>
              )}

              {/* Documents Section - IMPORTANT for workers */}
              <div className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Paperclip className="w-5 h-5 text-yellow-600" />
                  <p className="text-sm font-bold text-yellow-800 uppercase">
                    Job Documents ({attachments.length})
                  </p>
                </div>
                
                {loadingAttachments ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-yellow-600" />
                    <span className="ml-2 text-sm text-yellow-700">Loading documents...</span>
                  </div>
                ) : attachments.length > 0 ? (
                  <div>
                    <p className="text-xs text-yellow-700 mb-3">
                      Please review these documents before starting work.
                    </p>
                    <AttachmentList
                      attachments={attachments}
                      onDelete={null}
                      canDelete={false}
                      showUploader={true}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-yellow-700 italic">
                    No documents attached to this job yet.
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Worker Dashboard Component
export default function WorkerDashboard() {
  return (
    <DashboardLayout userType="worker">
      <Routes>
        <Route index element={<WorkerOverview />} />
        <Route path="calendar" element={<WorkerCalendar />} />
        <Route path="timesheets" element={<WorkerHistory />} />
        <Route path="assignments" element={<MyAssignments />} />
      </Routes>
    </DashboardLayout>
  );
}
